import "./texteditor.scss";
import { h } from "preact";
import { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from "preact/hooks";
import { TextEditorCore, type Pos } from "./texteditorcore";

export type Token = { type: string; text: string };

const LINE_HEIGHT = 20;
const SCROLL_MARGIN_LINES = 3; // Number of lines to keep visible above/below cursor when scrolling into view

export function TextEditor(props: {
    initialContent?: string,
    tokenizer?: (line: string) => Token[]
}) {
    const editor = useMemo(() => new TextEditorCore(), []);

    // #region UI State

    const [, forceUpdate] = useState({});

    const viewportRef = useRef<HTMLDivElement>(null);
    const charWidthRef = useRef<number>(0);
    const [gutterWidth, setGutterWidth] = useState(50);

    if (!viewportRef.current) {
        forceUpdate({});
    }

    useEffect(() => {
        editor.buffer.setContent(props.initialContent ?? "");
        forceUpdate({});
    }, [props.initialContent, forceUpdate]);

    // Measure char width inside the real DOM (perfect)
    const measureCharWidth = useCallback(() => {
        const vp = viewportRef.current;
        if (!vp) return null;
        const line = document.createElement("div");
        line.className = "line";
        line.style.position = "absolute";
        line.style.top = "-9999px";
        line.style.opacity = "0";
        line.style.pointerEvents = "none";
        line.innerHTML = '<span class="gutter">99999</span><pre class="code">' + "m".repeat(100) + "</pre>";
        vp.appendChild(line);
        const codeEl = line.querySelector(".code") as HTMLElement;
        const width = codeEl.getBoundingClientRect().width;
        vp.removeChild(line);
        return width / 100;
    }, []);

    useLayoutEffect(() => {
        if (charWidthRef.current > 0) return;
        const w = measureCharWidth();
        charWidthRef.current = w || 8.4;
    }, [measureCharWidth]);

    const charWidth = charWidthRef.current || 8.4;
    const totalHeight = editor.buffer.lineCount * LINE_HEIGHT;

    editor.linesPerPage = Math.floor((viewportRef.current?.clientHeight ?? 0) / LINE_HEIGHT);

    const overscan = 10;
    const scrollTop = viewportRef.current?.scrollTop || 0;
    const startIdx = Math.max(0, Math.floor(scrollTop / LINE_HEIGHT) - overscan);
    const endIdx = Math.min(editor.buffer.lineCount, Math.ceil((scrollTop + (viewportRef.current?.clientHeight ?? 0)) / LINE_HEIGHT) + overscan);
    const offsetY = startIdx * LINE_HEIGHT;

    const caretPos = {
        top: editor.cursor.line * LINE_HEIGHT,
        left: gutterWidth + editor.cursor.ch * charWidth,
    };

    // #endregion

    // #region UI Interaction

    const scrollCursorIntoView = useCallback((line: number) => {
        const vp = viewportRef.current;
        if (!vp) return;

        const cursorTop = line * LINE_HEIGHT;
        const cursorBottom = cursorTop + LINE_HEIGHT;
        const viewportTop = vp.scrollTop;
        const viewportBottom = viewportTop + vp.clientHeight;

        const marginPixels = SCROLL_MARGIN_LINES * LINE_HEIGHT;

        if (cursorTop < viewportTop) {
            // Cursor is above viewport - scroll up with margin
            const targetScroll = Math.max(0, cursorTop - marginPixels);
            vp.scrollTop = targetScroll;
        } else if (cursorBottom > viewportBottom) {
            // Cursor is below viewport - scroll down with margin
            const targetScroll = cursorBottom - vp.clientHeight + marginPixels;
            vp.scrollTop = targetScroll;
        }
    }, [editor]);

    const uiHandleKeyDown = useCallback(async (e: KeyboardEvent) => {
        const result = editor.handleKeyDown(e.key, e.shiftKey, e.ctrlKey || e.metaKey);
        if (result === false) return;

        e.preventDefault();

        if (result !== true) await result;
        forceUpdate({});
        console.log("forcing update");
    }, [editor, forceUpdate]);

    const uiHandleMouseMove = useCallback((e: MouseEvent) => {
        if (e.buttons !== 1 || !viewportRef.current) return;

        const rect = viewportRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + viewportRef.current.scrollLeft;
        const y = e.clientY - rect.top + viewportRef.current.scrollTop;
        const line = Math.min(Math.max(0, Math.floor(y / LINE_HEIGHT)), editor.buffer.lineCount - 1);
        const ch = Math.max(0, Math.min(Math.floor((x - gutterWidth) / charWidth), editor.buffer.getLine(line).length));

        editor.handleMouseMove({ line, ch });
        forceUpdate({});
    }, [editor, charWidth, gutterWidth]);

    const uiHandleMouseDown = useCallback((e: MouseEvent) => {
        if (e.button !== 0 || !viewportRef.current) return;

        // Check if click is on scrollbar (outside content area)
        const vp = viewportRef.current;
        const rect = vp.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // If clicking on scrollbar area, ignore
        if (clickX >= vp.clientWidth || clickY >= vp.clientHeight) {
            return;
        }

        const x = clickX + vp.scrollLeft;
        const y = clickY + vp.scrollTop;
        const line = Math.min(Math.max(0, Math.floor(y / LINE_HEIGHT)), editor.buffer.lineCount - 1);
        const ch = Math.max(0, Math.min(Math.floor((x - gutterWidth) / charWidth), editor.buffer.getLineLength(line)));

        editor.handleMouseDown({ line, ch }, e.shiftKey, e.ctrlKey);
        forceUpdate({});
    }, [editor, charWidth, gutterWidth]);

    const handleMouseUp = useCallback((e: MouseEvent) => {
        if (e.button !== 0) return;

        editor.handleMouseUp();
        forceUpdate({});
    }, [editor]);

    useEffect(() => {
        document.addEventListener("mousemove", uiHandleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("mousemove", uiHandleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [uiHandleMouseMove, handleMouseUp]);

    // #endregion

    // #region UI Rendering

    // Auto-scroll cursor into view after cursor changes
    useLayoutEffect(() => {
        scrollCursorIntoView(editor.cursor.line);
    }, [editor.cursor.line, scrollCursorIntoView]);

    // Force re-render on scroll to update visible lines
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp) return;
        const handler = () => forceUpdate({});
        vp.addEventListener("scroll", handler, { passive: true });
        return () => vp.removeEventListener("scroll", handler);
    }, []);

    const renderSelectionRects = useCallback(() => {
        const sel = editor.getNormalizedSelection();
        if (!sel) return null;
        const rects: { top: number; left: number; width: number; height: number }[] = [];

        if (sel.anchor.line === sel.head.line) {
            const left = gutterWidth + sel.anchor.ch * charWidth;
            const width = (sel.head.ch - sel.anchor.ch) * charWidth;
            rects.push({
                top: sel.anchor.line * LINE_HEIGHT,
                left,
                width,
                height: LINE_HEIGHT,
            });
        } else {
            // Multi-line selection
            for (let line = sel.anchor.line; line <= sel.head.line; line++) {
                const isFirst = line === sel.anchor.line;
                const isLast = line === sel.head.line;
                const lineText = editor.buffer.getLine(line);
                const left = gutterWidth + (isFirst ? sel.anchor.ch : 0) * charWidth;
                const width = isLast
                    ? sel.head.ch * charWidth
                    : (lineText.length - (isFirst ? sel.anchor.ch : 0)) * charWidth;

                rects.push({
                    top: line * LINE_HEIGHT,
                    left,
                    width: (width === 0 && !isFirst && !isLast) ? charWidth : width,
                    height: LINE_HEIGHT,
                });
            }
        }

        return rects.map((r, i) => (
            <div
                key={i}
                class="selection"
                style={{
                    top: `${r.top}px`,
                    left: `${r.left}px`,
                    width: `${r.width}px`,
                    height: `${r.height}px`,
                }}
            />
        ));
    }, [editor, gutterWidth, charWidth]);

    // #endregion

    return (
        <div
            class="ui-texteditor"
            tabIndex={0}
            onKeyDown={uiHandleKeyDown}
            onMouseDown={uiHandleMouseDown}
        >
            <div ref={viewportRef} class="viewport">
                <div class="lines" style={{ height: `${totalHeight}px` }}>
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        transform: `translateY(${offsetY}px)`
                    }}>
                        {Array.from({ length: endIdx - startIdx }, (_, i) => {
                            const lineIdx = startIdx + i;
                            const line = editor.buffer.getLine(lineIdx);
                            return (
                                <div key={lineIdx} class="line">
                                    <span class="gutter">{lineIdx + 1}</span>
                                    <pre>
                                        {props.tokenizer
                                            ? props.tokenizer(line).map((t, ti) => (
                                                <span key={ti} class={`token ${t.type || ""}`}>{t.text}</span>
                                            ))
                                            : <span class="token">{line}</span>}
                                    </pre>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {renderSelectionRects()}

                <div
                    key={`${editor.cursor.line}-${editor.cursor.ch}`}
                    class="caret"
                    style={{
                        top: `${caretPos.top}px`,
                        left: `${caretPos.left}px`,
                        height: `${LINE_HEIGHT}px`
                    }}
                />
            </div>
        </div>
    );
}
